/**
 * url操作
 */
import * as hosts from '../constants/hosts';
import { SESS_KEY } from '../request';

export type urlSearchParamMap = { [key: string]: any };

function toRealValue(inputVal: any) {
  let val = inputVal;

  if (Array.isArray(val)) {
    val = val[val.length - 1] || '';
  }

  if (!val) {
    return val;
  }
  if (val === 'undefined') {
    return undefined;
  }
  if (val === 'null') {
    return null;
  }
  if (val === 'true') {
    return true;
  }
  if (val === 'false') {
    return false;
  }
  if (/^\d*(\.\d*)?$/.test(val)) {
    const result = parseFloat(val);

    // 防止长度溢出
    if (result.toString() === val) {
      return result;
    }

    return val;
  }

  return val;
}

/**
 * 解析查询字符串
 * @param querystring
 */
export const parseQuerystring = (querystring: string) => {
  const query: { [key: string]: any } = {};

  if (querystring) {
    const pairs = querystring.split('&');

    pairs.forEach((pair: string) => {
      const equalIndex = pair.indexOf('=');
      let key = pair;
      let val = '';

      if (equalIndex >= 0) {
        key = pair.slice(0, equalIndex);
        val = pair.slice(equalIndex + 1) || '';
      }
      let decodedValue = val;

      try {
        decodedValue = decodeURIComponent(val);
      } catch (err) {
        decodedValue = val;
      }

      query[key] = toRealValue(decodedValue);
    });
  }

  return query;
};

/**
 * 解析查询字符串
 * @param querystring
 */
export const stringifyQuery = (query: Record<string, any>) => {
  const queryArr: string[] = [];

  Object.keys(query).forEach(key => {
    const val = query[key];

    queryArr.push(`${key}=${encodeURIComponent(val)}`);
  });

  return queryArr.join('&');
};

/**
 * 解析url
 */
export const parseUrl = (url: string) => {
  const [rest, hash = ''] = url.split('#');
  const arr = rest.split('?');

  const [hostPath, queryStr = ''] = arr;
  const matched = hostPath.match(/(https?:)?(\/\/)?([^/]*)(\/[^?]*)?/);
  const protocol = matched ? matched[1] || '' : '';
  const host = matched ? matched[3] : '';
  const pathname = matched ? matched[4] : '';
  const query = parseQuerystring(queryStr);

  return {
    protocol,
    host,
    pathname,
    query,
    hash,
    stringify() {
      // eslint-disable-next-line
      return stringifyUrl(
        this.protocol,
        this.host,
        this.pathname,
        this.query,
        this.hash
      );
    },
  };
};

/**
 * 转换成url字符串
 */
export const stringifyUrl = (
  protocol: string,
  host: string,
  pathname: string,
  query: { [key: string]: any },
  hash?: string
) => {
  const queryArr: string[] = [];

  Object.keys(query).forEach(key => {
    const val = query[key];

    // medgpt 小程序内嵌时 _opentype=null, 该情况可以通行
    if (val !== undefined && (val !== null || key === '_opentype')) {
      queryArr.push(`${key}=${encodeURIComponent(val)}`);
    }
  });

  if (host.endsWith('/')) {
    // eslint-disable-next-line no-param-reassign
    host = host.slice(0, host.length - 2);
  }

  let querystring = stringifyQuery(query);
  let hashStr = '';

  if (querystring) {
    querystring = `?${queryArr.join('&')}`;
  }

  if (hash && !hash.startsWith('#')) {
    hashStr = `#${hash}`;
  }

  const splitter = protocol && host ? '//' : '';

  return `${protocol}${splitter}${host}${
    pathname || '/'
  }${querystring}${hashStr}`;
};

/**
 * 给url追加参数，并返回新的url
 */
export const appendUrlParams = (url: string, query: { [key: string]: any }) => {
  const parsed = parseUrl(url);

  const nextQuery = { ...parsed.query, ...query };

  Object.keys(nextQuery).forEach(key => {
    if (nextQuery[key] === undefined) {
      delete nextQuery[key];
    }
  });

  parsed.query = nextQuery;

  return parsed.stringify();
};

/**
 * 生成 search
 */
export const createSearch = (
  query: { [key: string]: any },
  options: {
    oldSearch?: string;
    withPrefix?: boolean;
  }
) => {
  const { oldSearch = '', withPrefix = false } = options;
  const { query: oldQuery } = parseUrl(oldSearch);

  const nextQuery = { ...oldQuery, ...query };

  Object.keys(nextQuery).forEach(key => {
    if (nextQuery[key] === undefined) {
      delete nextQuery[key];
    }
  });

  const searchWithoutPrefix = stringifyQuery(nextQuery);

  if (!searchWithoutPrefix) return '';

  return `${withPrefix ? '?' : ''}${searchWithoutPrefix}`;
};

export function getCommonParams(
  query?: urlSearchParamMap,
  withSess?: boolean
): {
  _opentype?: string;
  channel?: string;
} {
  if (!query) {
    query = parseUrl(window.location.href).query;
  }

  const params: any = {};

  // opentype medgpt 小程序内嵌时传的是 null
  if (query._opentype || query._opentype === null) {
    // eslint-disable-next-line no-underscore-dangle
    params._opentype =
      query._opentype || (query._opentype === null ? null : '');
  }

  // 渠道号
  if (query.channel) {
    params.channel = query.channel || '';
  }

  const sessionSess = sessionStorage.getItem(SESS_KEY);

  if (!query.sess && withSess && sessionSess) {
    params.sess = sessionSess;
  }
  if (query.sess) {
    params.sess = query.sess;
  }
  if (query.source) {
    params.source = query.source;
  }

  return params;
}

export function getUrl(url: string, nextSearchQuery?: urlSearchParamMap) {
  return appendUrlParams(url, {
    ...getCommonParams(),
    ...(nextSearchQuery || {}),
  });
}

export function pushOldIH(path: string, query?: { [key: string]: any }) {
  const cp = getCommonParams();

  // 默认使用联友健康
  if (!cp._opentype) {
    cp._opentype = 'trans';
  }

  window.location.assign(
    `${hosts.WEB}/h5/ih2/?t=${new Date().getTime()}#${appendUrlParams(path, {
      ...getCommonParams(),
      ...query,
    })}`
  );
}

export function pushInquireH5(
  path: string,
  query?: { [key: string]: any },
  withSess?: boolean
) {
  window.location.assign(
    `${hosts.INQUIRY_H5}/#${appendUrlParams(path, {
      ...getCommonParams(undefined, withSess),
      ...query,
    })}`
  );
}

export function pushEmall({
  path,
  query,
}: {
  path: string;
  query?: { [key: string]: any };
}) {
  window.location.assign(
    `${hosts.WEB}/ih${appendUrlParams(path, {
      ...getCommonParams(),
      ...query,
    })}`
  );
}

/**
 * 前进并替换当前页
 */
export function replace(url: string, query?: { [key: string]: any }) {
  const href = appendUrlParams(url, {
    ...getCommonParams(),
    ...query,
  });

  window.location.replace(href);
}
